import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Drawer,
  Stack,
  Divider,
  Typography,
  IconButton,
  TextField,
  Alert,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  MenuItem,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";

import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { fetchData } from "../../../../fetchData";

import useHrmAbsenceStore from "../../../../stores/hrAbsence";
import useAuthStore from "../../../../stores/auth";
import DatePickerField from "../../../../components/DatePickerField";
import DragAndDropFiles from "../../../../components/DragAndDropFiles";
import FeatureFeedback from "../../../../components/FeatureFeedback";

interface FormValues {
  type: string;
  start_date: string;
  days: string;
  is_work_accident: string;
  had_previous_absence: string;
  observations: string;
  attachment?: any;
  include_dependent?: boolean;
}

const absenceTypes = [
  {
    value: "licenca_maternidade",
    label: "Licença maternidade",
  },
  {
    value: "licenca_paternidade",
    label: "Licença paternidade",
  },
  {
    value: "atestado",
    label: "Atestado",
  },
];

const HrmEmployeeAbsenceDrawer = () => {
  const company = useAuthStore((state) => state.company);
  const state = useHrmAbsenceStore((state) => state.drawer);
  const employee: any = useHrmAbsenceStore((state) => state.drawerData);
  const toggleDrawer = useHrmAbsenceStore((state) => state.toggleDrawer);
  const [activeStep, setActiveStep] = useState(0);
  const [err, setErr] = useState(false);
  const [success, setSuccess] = useState(false);
  const [progress, setProgress] = useState(false);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [absenceId, setAbsenceId] = useState<string | null>(null);
  const [shouldAskFeedback, setShouldAskFeedback] = useState(false);
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    trigger,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      type: "",
      start_date: "",
      days: "",
      is_work_accident: "",
      had_previous_absence: "",
      observations: "",
      attachment: null,
      include_dependent: false,
    },
  });

  const typeValue = watch("type");
  const startDateValue = watch("start_date");
  const daysValue = watch("days");

  const isMaternity = typeValue === "licenca_maternidade";
  const isPaternity = typeValue === "licenca_paternidade";
  const isCertificate = typeValue === "atestado";

  const getMinDate = () => {
    return dayjs().subtract(60, "day");
  };

  const getMaxDate = () => {
    return dayjs();
  };

  const calculateEndDate = () => {
    if (!startDateValue) return null;

    const startDate = dayjs(startDateValue);

    if (isPaternity) {
      return startDate.add(5, "day");
    }

    if (isMaternity) {
      return startDate.add(120, "day");
    }

    if (!daysValue) return null;
    const days = parseInt(daysValue);
    if (isNaN(days) || days <= 0) return null;
    return startDate.add(days, "day");
  };

  const checkIfPreviousMonth = (date: string) => {
    if (!date) return false;
    const selectedDate = dayjs(date);
    const currentDate = dayjs();
    return (
      selectedDate.month() < currentDate.month() ||
      selectedDate.year() < currentDate.year()
    );
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      const fieldsToValidate: (keyof FormValues)[] = [
        "type",
        "start_date",
        "had_previous_absence",
      ];

      if (isCertificate) {
        fieldsToValidate.push("is_work_accident");
        fieldsToValidate.push("days");
        fieldsToValidate.push("attachment");
      }

      if (isMaternity || isPaternity) {
        fieldsToValidate.push("attachment");
      }

      if (
        !(await trigger(fieldsToValidate, {
          shouldFocus: true,
        }))
      )
        return;

      const startDate = getValues("start_date");

      if (checkIfPreviousMonth(startDate)) {
        setWarningMessage(
          "Caro cliente,\n\nGostaríamos de informar que o atestado encaminhado pode gerar alguma alteração na folha de pagamento, seja da competência atual ou de meses anteriores, caso impacte os cálculos.\n\nFique tranquilo(a), estamos acompanhando tudo e, se houver qualquer ajuste necessário, avisaremos você.",
        );
        setWarningDialogOpen(true);
        return;
      }

      if (activeStep + 1 < steps.length)
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else {
      if (activeStep + 1 < steps.length)
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const ErrorAlert = () => {
    return (
      <Alert
        severity="error"
        sx={{
          flexDirection: "column",
          alignItems: "center",
          textAlign: "cenrer",
        }}
      >
        <Stack alignItems="center" direction="column">
          <Typography textAlign="center" variant="h5">
            Ops!
          </Typography>
          <Typography textAlign="center">
            Não foi possível enviar a solicitação no momento. Tente novamente
            mais tarde.
          </Typography>
          <Button
            variant="contained"
            onClick={() => toggleDrawer()}
            sx={{ mt: 2 }}
          >
            Fechar
          </Button>
        </Stack>
      </Alert>
    );
  };

  const SuccessAlert = () => {
    return (
      <>
        {!shouldAskFeedback && (
          <Alert
            severity="success"
            sx={{
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <Stack alignItems="center" direction="column">
              <Typography textAlign="center" variant="h5">
                Muito bem!
              </Typography>
              <Typography textAlign="center">
                Solicitação enviada com sucesso.
              </Typography>
              <Button
                variant="contained"
                onClick={() => toggleDrawer()}
                sx={{ mt: 2 }}
              >
                Fechar
              </Button>
            </Stack>
          </Alert>
        )}

        {shouldAskFeedback && (
          <FeatureFeedback
            submitting={progress}
            onSubmit={async ({ rating, feedback }) => {
              setProgress(true);
              try {
                await fetchData("/feedback/ces", {
                  method: "POST",
                  body: JSON.stringify({
                    context: "ces_fopag_absence",
                    rating,
                    comments: feedback || null,
                    clientId: company._id,
                    clientReference: company.reference,
                  }),
                });
              } catch (error) {
                console.log(error);
              }
              setProgress(false);
            }}
            onSkip={async () => {
              setProgress(true);
              try {
                await fetchData("/feedback/skip", {
                  method: "POST",
                  body: JSON.stringify({ context: "ces_fopag_absence" }),
                });
              } catch (error) {
                console.log(error);
              }
              setProgress(false);
              toggleDrawer();
            }}
            onClose={() => toggleDrawer()}
          />
        )}
      </>
    );
  };

  const Progress = () => {
    return (
      <Alert
        severity="info"
        sx={{
          flexDirection: "column",
          alignItems: "center",
          textAlign: "cenrer",
        }}
        icon={false}
      >
        <Stack spacing={1} alignItems="center" direction="column">
          <CircularProgress />
          <Typography textAlign="center" variant="h6">
            Enviando solicitação
          </Typography>
          <Typography textAlign="center">Aguarde...</Typography>
        </Stack>
      </Alert>
    );
  };

  const steps = [
    {
      label: "Solicitação",
      description: ``,
      outlet: (
        <Stack spacing={1}>
          <TextField
            select
            {...register("type", {
              required: "Selecione o tipo de atestado/afastamento",
            })}
            label="Tipo"
            variant="filled"
            required
            value={watch("type") || ""}
          >
            {absenceTypes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {errors?.type && (
            <Alert severity="error">{errors.type.message?.toString()}</Alert>
          )}

          <DatePickerField
            name="start_date"
            register={register}
            get={getValues}
            set={setValue}
            label="Data Início"
            variant="filled"
            required
            requiredtxt="Informe a data de início."
            minDate={getMinDate()}
            maxDate={getMaxDate()}
            disabled={!typeValue}
          />
          {errors?.start_date && (
            <Alert severity="error">
              {errors.start_date.message?.toString()}
            </Alert>
          )}

          {isCertificate && (
            <>
              <Controller
                name="is_work_accident"
                control={control}
                rules={{ required: "Selecione se é acidente de trabalho" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    required
                    label="Acidente de trabalho?"
                    variant="filled"
                    error={!!errors.is_work_accident}
                  >
                    <MenuItem value="SIM">SIM</MenuItem>
                    <MenuItem value="NÃO">NÃO</MenuItem>
                  </TextField>
                )}
              />
              {errors?.is_work_accident && (
                <Alert severity="error">
                  {errors.is_work_accident.message?.toString()}
                </Alert>
              )}

              <TextField
                required
                {...register("days", {
                  required: "Informe a quantidade de dias",
                  validate: (value) => {
                    const numValue = Number(value);
                    if (isNaN(numValue) || numValue <= 0) {
                      return "A quantidade de dias deve ser maior que zero";
                    }
                    return true;
                  },
                })}
                type="number"
                label="Quantidade de Dias"
                variant="filled"
                disabled={!startDateValue}
              />
              {errors?.days && (
                <Alert severity="error">
                  {errors.days.message?.toString()}
                </Alert>
              )}

              {startDateValue && calculateEndDate() && (
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Data Fim:</strong>{" "}
                    {calculateEndDate()?.format("DD/MM/YYYY")}
                  </Typography>
                </Alert>
              )}
            </>
          )}

          {(isMaternity || isPaternity) &&
            startDateValue &&
            calculateEndDate() && (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Data Fim:</strong>{" "}
                  {calculateEndDate()?.format("DD/MM/YYYY")}
                </Typography>
              </Alert>
            )}

          <Controller
            name="had_previous_absence"
            control={control}
            rules={{
              required: "Selecione se houve afastamentos nos últimos 60 dias",
            }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                required
                label="Afastamentos nos últimos 60 dias?"
                variant="filled"
                error={!!errors.had_previous_absence}
                helperText="Funcionário teve afastamentos nos últimos 60 dias?"
              >
                <MenuItem value="SIM">SIM</MenuItem>
                <MenuItem value="NÃO">NÃO</MenuItem>
              </TextField>
            )}
          />
          {errors?.had_previous_absence && (
            <Alert severity="error">
              {errors.had_previous_absence.message?.toString()}
            </Alert>
          )}

          <TextField
            {...register("observations")}
            label="Observações"
            variant="filled"
            multiline
            rows={4}
          />

          {isPaternity && (
            <>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" fontWeight={600}>
                  Certidão de Nascimento
                </Typography>
                <Tooltip
                  title="Anexe a certidão de nascimento do filho(a)"
                  arrow
                >
                  <HelpOutlineIcon
                    sx={{
                      fontSize: 16,
                      color: "primary.main",
                      cursor: "help",
                    }}
                  />
                </Tooltip>
              </Stack>
              <Controller
                name="attachment"
                control={control}
                rules={{
                  validate: (value) => {
                    if (isPaternity && !value) {
                      return "Certidão de nascimento é obrigatória para licença paternidade";
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <DragAndDropFiles
                    baseUrl={`cloud/${company._id}?path=hr_processes/absence/${
                      employee.i_empregados
                    }/paternity/${
                      absenceId ||
                      `${Date.now()}-${Math.random()
                        .toString(36)
                        .substring(2, 9)}`
                    }`}
                    multiple={false}
                    allowedFiles=".pdf,.jpeg,.jpg,.png,.doc,.docx"
                    callback={(response) => {
                      setUploadedFile(response.data);
                      field.onChange(response.data);
                      trigger("attachment");
                    }}
                  />
                )}
              />
              {errors?.attachment && (
                <Alert severity="error">
                  {errors.attachment.message?.toString()}
                </Alert>
              )}
              {!uploadedFile && isPaternity && (
                <Typography variant="caption" color="error">
                  Certidão de nascimento obrigatória
                </Typography>
              )}
            </>
          )}

          {isMaternity && (
            <>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" fontWeight={600}>
                  Atestado
                </Typography>
                <Tooltip title="Anexe o atestado médico" arrow>
                  <HelpOutlineIcon
                    sx={{
                      fontSize: 16,
                      color: "primary.main",
                      cursor: "help",
                    }}
                  />
                </Tooltip>
              </Stack>
              <Controller
                name="attachment"
                control={control}
                rules={{
                  validate: (value) => {
                    if (isMaternity && !value) {
                      return "Atestado é obrigatório para licença maternidade";
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <DragAndDropFiles
                    baseUrl={`cloud/${company._id}?path=hr_processes/absence/${
                      employee.i_empregados
                    }/maternity/${
                      absenceId ||
                      `${Date.now()}-${Math.random()
                        .toString(36)
                        .substring(2, 9)}`
                    }`}
                    multiple={false}
                    allowedFiles=".pdf,.jpeg,.jpg,.png,.doc,.docx"
                    callback={(response) => {
                      setUploadedFile(response.data);
                      field.onChange(response.data);
                      trigger("attachment");
                    }}
                  />
                )}
              />
              {errors?.attachment && (
                <Alert severity="error">
                  {errors.attachment.message?.toString()}
                </Alert>
              )}
              {!uploadedFile && isMaternity && (
                <Typography variant="caption" color="error">
                  Atestado obrigatório
                </Typography>
              )}

              <FormControlLabel
                control={
                  <Controller
                    name="include_dependent"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                }
                label="Incluir dependente?"
              />
            </>
          )}

          {isCertificate && (
            <>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" fontWeight={600}>
                  Anexo
                </Typography>
                <Tooltip
                  title="Anexe documentos relacionados ao atestado"
                  arrow
                >
                  <HelpOutlineIcon
                    sx={{
                      fontSize: 16,
                      color: "primary.main",
                      cursor: "help",
                    }}
                  />
                </Tooltip>
              </Stack>
              <Controller
                name="attachment"
                control={control}
                rules={{
                  validate: (value) => {
                    if (isCertificate && !value) {
                      return "Anexo é obrigatório para atestado";
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <DragAndDropFiles
                    baseUrl={`cloud/${company._id}?path=hr_processes/absence/${
                      employee.i_empregados
                    }/certificate/${
                      absenceId ||
                      `${Date.now()}-${Math.random()
                        .toString(36)
                        .substring(2, 9)}`
                    }`}
                    multiple={false}
                    allowedFiles=".pdf,.jpeg,.jpg,.png,.doc,.docx"
                    callback={(response) => {
                      setUploadedFile(response.data);
                      field.onChange(response.data);
                      trigger("attachment");
                    }}
                  />
                )}
              />
              {errors?.attachment && (
                <Alert severity="error">
                  {errors.attachment.message?.toString()}
                </Alert>
              )}
              {!uploadedFile && isCertificate && (
                <Typography variant="caption" color="error">
                  Anexo obrigatório
                </Typography>
              )}
            </>
          )}
        </Stack>
      ),
    },
    {
      label: "Envio",
      description: "",
      outlet: <></>,
    },
  ];

  const handleWarningDialogClose = async (proceed: boolean) => {
    setWarningDialogOpen(false);
    if (proceed) {
      if (activeStep + 1 < steps.length) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    }
  };

  const submitForm = async () => {
    setProgress(true);
    const formData = getValues();
    const data: any = {
      ...formData,
      name: employee.nome,
      reference: company.reference,
    };

    if (calculateEndDate()) {
      data.end_date = calculateEndDate()?.toISOString();
    }

    if (uploadedFile) {
      data.attachment = uploadedFile;
    }

    try {
      await fetchData(`/hrm/fopag/employee/${employee.i_empregados}/absence`, {
        method: "POST",
        body: JSON.stringify(data),
      });
      setSuccess(true);
      try {
        const feedbackState = await fetchData("/feedback/track", {
          method: "POST",
          body: JSON.stringify({ context: "ces_fopag_absence" }),
        });
        setShouldAskFeedback(!!feedbackState?.shouldAsk);
      } catch (error) {
        console.log(error);
        setShouldAskFeedback(false);
      }
    } catch (error) {
      setErr(true);
      console.log(error);
    }

    setProgress(false);
  };

  async function onSubmit() {
    if (activeStep === 1 && !success) {
      await submitForm();
    }
  }

  useEffect(() => {
    if (!state) {
      reset();
      setActiveStep(0);
      setSuccess(false);
      setErr(false);
      setWarningDialogOpen(false);
      setUploadedFile(null);
      setAbsenceId(null);
      setShouldAskFeedback(false);
    } else {
      const uniqueId = `${Date.now()}-${Math.random()
        .toString(36)
        .substring(2, 9)}`;
      setAbsenceId(uniqueId);
    }
  }, [state, reset]);

  useEffect(() => {
    if (!typeValue) {
      setValue("is_work_accident", "");
      setValue("days", "");
      setValue("include_dependent", false);
      setValue("attachment", null);
      setUploadedFile(null);
      return;
    }

    if (typeValue !== "atestado") {
      setValue("is_work_accident", "");
      setValue("days", "");
    }

    if (typeValue !== "licenca_maternidade") {
      setValue("include_dependent", false);
    }

    if (
      typeValue !== "licenca_paternidade" &&
      typeValue !== "licenca_maternidade" &&
      typeValue !== "atestado"
    ) {
      setValue("attachment", null);
      setUploadedFile(null);
    }
  }, [typeValue, setValue]);

  return (
    <>
      <Drawer
        anchor="left"
        open={state}
        onClose={() => {
          toggleDrawer();
        }}
      >
        <Box sx={{ p: 3, pt: 2, width: 400 }} width={300} height="100%">
          <Stack height="100%" spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Stack
                direction="row"
                spacing={1}
                sx={{ flex: 1 }}
                alignItems="center"
              >
                <Typography variant="body1">
                  Atestados & Afastamentos
                </Typography>
              </Stack>
              <IconButton
                onClick={() => {
                  toggleDrawer();
                }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body1" fontWeight={600}>
              {employee.nome}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((step, index) => (
                  <Step key={step.label}>
                    <StepLabel>{step.label}</StepLabel>
                    <StepContent>
                      {step.description && (
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          {step.description}
                        </Typography>
                      )}
                      {step.outlet}
                      {index === 0 && (
                        <Box sx={{ mb: 2 }}>
                          <div>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={async (e) => {
                                e.preventDefault();
                                await handleNext();
                              }}
                              sx={{ mt: 1, mr: 1 }}
                              type="button"
                            >
                              Próximo
                            </Button>
                          </div>
                        </Box>
                      )}
                      {index === 1 && !success && (
                        <Box sx={{ mb: 2 }}>
                          <div>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={async (e) => {
                                e.preventDefault();
                                await onSubmit();
                              }}
                              sx={{ mt: 1, mr: 1 }}
                              type="button"
                              disabled={progress}
                            >
                              Enviar
                            </Button>
                            <Button
                              size="small"
                              onClick={handleBack}
                              sx={{ mt: 1, mr: 1 }}
                              type="button"
                              disabled={progress}
                            >
                              Voltar
                            </Button>
                          </div>
                        </Box>
                      )}
                      {index === 1 && success && (
                        <>
                          <SuccessAlert />
                        </>
                      )}
                      {index === 1 && err && <ErrorAlert />}
                      {index === 1 && progress && <Progress />}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </form>
          </Stack>
        </Box>
      </Drawer>

      <Dialog
        open={warningDialogOpen}
        onClose={() => handleWarningDialogClose(false)}
      >
        <DialogTitle>Atenção</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: "pre-line" }}>
            {warningMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleWarningDialogClose(false)}>
            Cancelar
          </Button>
          <Button onClick={() => handleWarningDialogClose(true)} autoFocus>
            Continuar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HrmEmployeeAbsenceDrawer;
